import { Tab } from "@headlessui/react";
import { useParams } from "react-router-dom";

import { WorkflowVersion } from "../Workflows/types";
import VersionSelectorShimmer from "./VersionSelectorShimmer";

import { ListPromptVersion } from "@/types";
import { ReportScore } from "@/types/evaluate";
import { DragDropContext } from "@hello-pangea/dnd";
import { InfiniteQueryObserverBaseResult } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  ConfirmationModal,
  ConfirmationModalVariant,
} from "../ConfirmationModal";
import ExperimentModal from "../ExperimentModal";
import { LabelsSelect } from "../LabelsSelect";
import {
  useABTestManagement,
  useExperimentModal,
  useInfiniteScroll,
  useLabelDragAndDrop,
  useLabelManagement,
  useOptimisticVersions,
} from "./hooks";
import useKeyboardNavigation from "./hooks/useKeyboardNavigation";
import useScrollToCurrentVersion from "./hooks/useScrollToCurrentVersion";
import VersionTab from "./VersionTab";

export interface VersionSelectorProps {
  versions?: (WorkflowVersion | ListPromptVersion)[];
  dynamicReleaseLabelsRegistry?: {
    [version: number]: { name: string; id: number }[];
  };
  versionEvaluationScoresRegistry?: { [key: number]: ReportScore | null };
  showVersionList: boolean;
  setShowVersionList: (value: boolean) => void;
  onAddReleaseLabel?: (label: string, versionId: number) => Promise<unknown>;
  onRemoveReleaseLabel?: (labelId: number) => Promise<unknown>;
  onMoveReleaseLabel?: (
    labelId: number,
    labelName: string,
    targetVersionId: number,
  ) => Promise<unknown>;
  counts?: { [index: number]: number };
  navigateToVersionLink: (versionNumber: number) => string;
  fetchNextPage?: InfiniteQueryObserverBaseResult["fetchNextPage"];
  className?: string;
  isWorkflow?: boolean;
}

const VersionSelector = ({
  versions,
  showVersionList,
  setShowVersionList,
  onAddReleaseLabel = async () => {},
  onRemoveReleaseLabel = async () => {},
  onMoveReleaseLabel = async () => {},
  dynamicReleaseLabelsRegistry = {},
  versionEvaluationScoresRegistry = {},
  counts = {},
  navigateToVersionLink,
  isWorkflow = false,
  fetchNextPage,
  className,
}: VersionSelectorProps) => {
  const { versionId: versionNumber, promptId } = useParams();

  const allLabels = useMemo(
    () => versions?.map((v) => v.labels).flat() || [],
    [versions],
  );

  const { isLoadingMore, loadingRef, tabListRef } =
    useInfiniteScroll(fetchNextPage);

  const {
    selectedReleaseLabelGroupId,
    isExperimentModalOpen,
    setExperimentModalOpen,
    handleDynamicReleaseLabelClick,
  } = useExperimentModal();

  const {
    versionIdForABTest,
    labelsSelectOpen,
    setLabelsSelectOpen,
    handleABTestButtonClick,
  } = useABTestManagement(promptId);

  const {
    labelToDelete,
    setLabelToDelete,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
  } = useLabelManagement();

  const {
    isDragging,
    labelMoveConfirmation,
    setLabelMoveConfirmation,
    handleDragStart,
    handleDragEnd,
    executeReleaseMove,
    pendingLabelMoves,
  } = useLabelDragAndDrop(versions, onMoveReleaseLabel);

  const { getAugmentedVersions } = useOptimisticVersions(
    versions,
    pendingLabelMoves,
  );

  useKeyboardNavigation({
    versions: versions || [],
    activeVersionNumber: versionNumber ? Number(versionNumber) : null,
    navigateToVersionLink,
  });

  useScrollToCurrentVersion({
    activeVersionNumber: versionNumber ? Number(versionNumber) : null,
    versions: versions || [],
    containerRef: tabListRef,
  });

  if (!versions) return <VersionSelectorShimmer />;
  if (!showVersionList) return null;

  const augmentedVersions = getAugmentedVersions();

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
        <Tab.Group>
          <div
            className={`relative flex min-w-[300px] max-w-[25%] flex-col rounded border ${
              className || ""
            }`}
          >
            <div className="flex items-center justify-between gap-x-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-1">
              <button
                className="text-xs text-gray-500 hover:text-gray-700"
                onClick={() => setShowVersionList(!showVersionList)}
              >
                Hide
              </button>
              {isDragging && (
                <span className="text-xs font-medium text-blue-600">
                  Drop label on a version to move it
                </span>
              )}
            </div>
            <Tab.List
              className="relative max-h-full overflow-y-auto"
              ref={tabListRef}
            >
              {augmentedVersions.map((version: any) => {
                const isActiveVersion =
                  Number(versionNumber) === version.number;

                return (
                  <VersionTab
                    key={version.id}
                    version={version}
                    isActiveVersion={isActiveVersion}
                    versionNumber={versionNumber}
                    counts={counts}
                    navigateToVersionLink={navigateToVersionLink}
                    onAddReleaseLabel={onAddReleaseLabel}
                    handleDynamicReleaseLabelClick={
                      handleDynamicReleaseLabelClick
                    }
                    dynamicReleaseLabelsRegistry={dynamicReleaseLabelsRegistry}
                    versionEvaluationScoresRegistry={
                      versionEvaluationScoresRegistry
                    }
                    handleABTestButtonClick={(promptId, versionId) =>
                      handleABTestButtonClick(promptId, versionId, allLabels)
                    }
                    promptId={promptId}
                    isWorkflow={isWorkflow}
                    setLabelToDelete={setLabelToDelete}
                    setIsConfirmationModalOpen={setIsConfirmationModalOpen}
                    allLabels={allLabels}
                  />
                );
              })}
              {fetchNextPage && (
                <div
                  ref={loadingRef}
                  className="h-10 w-full py-3"
                  style={{ minHeight: "40px" }}
                >
                  {isLoadingMore && (
                    <div className="flex justify-center py-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500"></div>
                      <span className="ml-2 text-xs text-gray-500">
                        Loading more versions...
                      </span>
                    </div>
                  )}
                </div>
              )}
            </Tab.List>
          </div>
        </Tab.Group>
      </DragDropContext>
      {isExperimentModalOpen && selectedReleaseLabelGroupId && (
        <ExperimentModal
          promptId={promptId}
          releaseLabelGroupId={selectedReleaseLabelGroupId}
          setOpen={setExperimentModalOpen}
        />
      )}
      <ConfirmationModal
        title={"Are you sure you want to remove this label?"}
        MarkdownDescription={
          <>
            {`Release label `} <b>{`"${labelToDelete?.name}"`}</b>
            {` will be removed.`}
          </>
        }
        saveButtonVariant={ConfirmationModalVariant.destructive}
        isOpen={isConfirmationModalOpen && !!labelToDelete}
        headerVariant={ConfirmationModalVariant.destructive}
        setIsOpen={setIsConfirmationModalOpen}
        leaveButtonText={"Cancel"}
        saveButtonText={"Remove Label"}
        useBlocker={false}
        handleOnConfirm={function (): void {
          labelToDelete && onRemoveReleaseLabel(labelToDelete.id);
          setIsConfirmationModalOpen(false);
        }}
      />
      <ConfirmationModal
        title={"Are you sure you want to move this label?"}
        MarkdownDescription={
          <>
            {`Release label `} <b>{`"${labelMoveConfirmation.labelName}"`}</b>
            {` will be moved to version `}
            <b>{`${versions?.find(
              (v) => v.id === labelMoveConfirmation.targetVersionId,
            )?.number}`}</b>
            .
          </>
        }
        saveButtonVariant={"default"}
        isOpen={labelMoveConfirmation.isOpen}
        headerVariant={ConfirmationModalVariant.warning}
        setIsOpen={(isOpen) =>
          setLabelMoveConfirmation((prev) => ({ ...prev, isOpen }))
        }
        leaveButtonText={"Cancel"}
        saveButtonText={"Move Label"}
        useBlocker={false}
        handleOnConfirm={executeReleaseMove}
      />
      <LabelsSelect
        promptVersion={{
          prompt_id: Number(promptId),
          version_id: versionIdForABTest!,
        }}
        open={labelsSelectOpen}
        labels={allLabels as any}
        isLoading={false}
        setOpen={setLabelsSelectOpen}
      />
    </>
  );
};

export default VersionSelector;
